import { api } from "./client";

export type Sex = "male" | "female" | "other";

export interface PatientRead {
  id: string;
  clinic_id: string;
  card_number: string;
  card_qr_token: string;
  card_barcode: string;
  family_name: string;
  given_name: string;
  father_name: string | null;
  birth_date: string;
  sex: Sex;
  age: number;
  jshshir: string | null;
  phone_primary: string;
  photo_object_key: string | null;
  workplace: string | null;
  position: string | null;
  created_at: string;
}

export interface PatientDetail extends PatientRead {
  passport_seriya: string | null;
  passport_number: string | null;
  address_region: string | null;
  address_district: string | null;
  address_full: string | null;
  phone_secondary: string | null;
  email: string | null;
  consent_signed_at: string | null;
  created_by_user_id: string;
  updated_at: string;
}

export interface PatientList {
  items: PatientRead[];
  total: number;
  limit: number;
  offset: number;
}

export interface PatientCreatePayload {
  family_name: string;
  given_name: string;
  father_name?: string | null;
  birth_date: string;
  sex: Sex;
  jshshir?: string | null;
  passport_seriya?: string | null;
  passport_number?: string | null;
  address_region?: string | null;
  address_district?: string | null;
  address_full?: string | null;
  phone_primary: string;
  phone_secondary?: string | null;
  email?: string | null;
  workplace?: string | null;
  position?: string | null;
  consent_signed: boolean;
}

export async function listPatients(params: {
  q?: string;
  card?: string;
  sex?: Sex;
  birth_year?: number;
  limit?: number;
  offset?: number;
}): Promise<PatientList> {
  const { data } = await api.get<PatientList>("/patients", { params });
  return data;
}

export async function createPatient(payload: PatientCreatePayload): Promise<PatientDetail> {
  const { data } = await api.post<PatientDetail>("/patients", payload);
  return data;
}

export type PatientUpdatePayload = Partial<Omit<PatientCreatePayload, "consent_signed">>;

export async function updatePatient(
  id: string,
  payload: PatientUpdatePayload,
): Promise<PatientDetail> {
  const { data } = await api.patch<PatientDetail>(`/patients/${id}`, payload);
  return data;
}

export async function getPatient(id: string): Promise<PatientDetail> {
  const { data } = await api.get<PatientDetail>(`/patients/${id}`);
  return data;
}

export async function deletePatient(id: string, reason: string): Promise<void> {
  await api.delete(`/patients/${id}`, { data: { reason } });
}

export async function downloadCardPdf(id: string, lang: string): Promise<Blob> {
  const { data } = await api.get<Blob>(`/patients/${id}/card-pdf`, {
    params: { lang },
    responseType: "blob",
  });
  return data;
}

export async function uploadPhoto(
  id: string,
  file: File,
): Promise<{ photo_object_key: string }> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<{ photo_object_key: string }>(
    `/patients/${id}/photo`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}
